<template>
  <section class="change-password-form">
    <div class="change-password-form__container" v-loading="pageLoading">
      <header class="change-password-form__header">
        <h1 class="change-password-form__title" v-if="mustChangePassword">
          {{ $t('recovery_password') }}
        </h1>
        <div v-else-if="!changePasswordSuccess">
          {{ $t('validation_error_password') }}
        </div>
      </header>

      <div class="change-password-form__content">
        <ElForm
          v-if="changePasswordSuccess === null && !backendFail"
          ref="form"
          :model="form"
          :rules="rules"
        >
          <ElFormItem prop="password">
            <ElInput v-model="form.password" type="password" :placeholder="$t('password')" />
          </ElFormItem>

          <ElFormItem prop="confirm_password">
            <ElInput
              v-model="form.confirm_password"
              type="password"
              :placeholder="$t('confirm_password')"
            />
          </ElFormItem>

          <ElButton class="change-password-form__btn--fluid" type="primary" @click="submitForm">
            {{ $t('change_password_button') }}
          </ElButton>
        </ElForm>

        <div class="change-password-form__change-succeeded" v-if="changePasswordSuccess">
          <SuccessButton />

          <p class="change-password-form__message">
            {{ $t('change_password_success') }}
          </p>

          <ElButton class="change-password-form__btn--fluid" type="primary" @click="goToLogin">
            {{ $t('login') }}
          </ElButton>
        </div>

        <div class="change-password-form__link-not-active" v-if="backendFail">
          <p class="change-password-form__message">
            {{ $t('link_is_not_active') }}
          </p>

          <RouterLink to="/reset">
            <ElButton type="primary" class="change-password-form__btn--fluid">
              {{ $t('change_password') }}
            </ElButton>
          </RouterLink>
        </div>
      </div>

      <footer
        :class="{ 'change-password-form__footer--ja': $i18n.locale === 'ja' }"
        class="change-password-form__footer"
      >
        {{ $t('return_to_login') }}
        <RouterLink to="/login">{{ $t('sign_in') }}</RouterLink>
      </footer>
    </div>
  </section>
</template>

<script>
import SuccessButton from '../components/Success.vue';

export default {
  components: {
    SuccessButton,
  },

  async created() {
    const { token } = this.$route.params;
    this.form.token = token;
    const resettingData = (await this.getResettingDataByToken(token))?.data;
    if (!resettingData?.mustChangePassword) {
      return;
    }

    this.$alert(this.$t('alert_change_password_message'), this.$t('alert_change_password_title'), {
      confirmButtonText: this.$t('alert_change_password_button'),
    });
  },

  data() {
    const validatePassEqual = (rule, value, callback) => {
      if (value === '') {
        callback(new Error(this.$t('validation_password')));
      } else if (value !== this.form.password) {
        callback(new Error(this.$t('passwords_must_match')));
      } else {
        callback();
      }
    };

    return {
      pageLoading: false,
      backendFail: null,
      changePasswordSuccess: null,
      userLoaded: false,
      mustChangePassword: false,
      form: {
        token: null,
        password: '',
        confirm_password: '',
      },
      rules: {
        password: [
          {
            required: true,
            message: this.$t('validation_password'),
            trigger: 'blur',
          },
          {
            range: true,
            min: 6,
            message: this.$t('validation_password_length', { n: 6 }),
            trigger: 'blur',
          },
        ],
        confirm_password: [{ validator: validatePassEqual, trigger: 'blur' }],
      },
    };
  },

  methods: {
    submitForm() {
      this.$refs.form.validate(async valid => {
        if (valid) {
          await this.changePassword();
        }
      });
    },

    async changePassword() {
      try {
        this.pageLoading = true;

        await this.$http.post(`/resetting/password`, this.form);
        this.changePasswordSuccess = true;
      } catch (e) {
        if (e.response?.data?.errors) {
          this.applyServerValidation(e.response.data.errors);
        } else {
          this.processChangePasswordError(e);
        }
      } finally {
        this.pageLoading = false;
      }
    },

    applyServerValidation(errors = {}) {
      // Server sends only English messages -> translation is here by the errors keys
      // confirm_password fields uses because it's last in the list of the fields
      this.setFieldError(
        'confirm_password',
        Object.keys(errors)
          .map(fieldName => this.$t('validation_error_' + fieldName))
          .join('\n')
      );
    },

    setFieldError(fieldName, errorMessage) {
      const field = this.$refs.form.fields.find(field => field.prop === fieldName);
      if (field) {
        field.validateMessage = errorMessage;
        field.validateState = 'error';
      }
    },

    processChangePasswordError(e) {
      this.backendFail = true;
    },

    async getResettingDataByToken(token) {
      try {
        this.pageLoading = true;
        return (await this.$http.get(`/resetting/${token}`)).data;
      } catch (e) {
        this.processUserGettingError(e);
      } finally {
        this.pageLoading = false;
      }
    },

    processUserGettingError(e) {
      this.backendFail = true;
    },

    goToLogin() {
      this.$router.push({ name: 'login' });
    },
  },
  i18n: {
    messages: {
      ru: {
        sign_in: 'Входa',
        change_password: 'Смена пароля',
        link_is_not_active: 'Данная ссылка не активна, отправьте запрос на смену пароля повторно',
        recovery_password: 'Восстановление пароля',
        return_to_login: 'Вернуться на страницу',
        change_password_button: 'Сменить пароль',
        change_password_success: 'Пароль успешно изменён',
        validation_error_password:
          'Новый пароль должен содержать не менее 12 символов, включая заглавные и строчные буквы, цифры и спецсимволы',
        alert_change_password_title: 'Время защитить свою учетную запись',
        alert_change_password_message:
          'Ваш пароль больше не соответствует обновлённым требованиям безопасности. Вы были автоматически выведены из системы. Пожалуйста, создайте новый пароль, чтобы восстановить доступ.',
        alert_change_password_button: 'Обновить пароль',
      },
      uk: {
        sign_in: 'Входу',
        change_password: 'Зміна пароля',
        recovery_password: 'Відновлення пароля',
        return_to_login: 'Повернутися на сторінку',
        link_is_not_active: 'Дане посилання не активне, надішліть запит на зміну пароля повторно',
        change_password_button: 'Змінити пароль',
        change_password_success: 'Пароль успішно змінено',
        validation_error_password:
          'Новий пароль повинен мати щонайменше 12 символів, включати великі й малі літери, цифри та спецсимволи',
        alert_change_password_title: 'Час захистити ваш обліковий запис',
        alert_change_password_message:
          'Ваш пароль більше не відповідає оновленим вимогам безпеки. Вас було автоматично вилоговано. Будь ласка, створіть новий пароль, щоб відновити доступ до системи.',
        alert_change_password_button: 'Оновити пароль',
      },
      en: {
        sign_in: 'Login',
        change_password: 'Change password',
        recovery_password: 'Password recovery',
        return_to_login: 'Return to the page',
        link_is_not_active:
          'This link is not active, submit a request to change the password again',
        change_password_button: 'Change the password',
        change_password_success: 'Password successfully changed',
        validation_error_password:
          'New password must be at least 12 characters and include uppercase, lowercase letters, digits, and special symbols',
        alert_change_password_title: 'Time to secure your account',
        alert_change_password_message:
          'Your password no longer meets updated security requirements. You have been automatically logged out. Please create a new password to regain access to the system.',
        alert_change_password_button: 'Update Password',
      },
      ja: {
        sign_in: 'ログイン',
        change_password: 'パスワード変更',
        recovery_password: 'パスワードの回復',
        return_to_login: 'ページに戻る',
        link_is_not_active:
          'このリンクはアクティブではありません。パスワード変更のリクエストを再度送信してください。',
        change_password_button: 'パスワードを変更する',
        change_password_success: 'パスワードが正常に変更されました',
        validation_error_password:
          '新しいパスワードは12文字以上で、大文字、小文字、数字、特殊文字を含める必要があります。',
        alert_change_password_title: 'アカウントを保護する時間です',
        alert_change_password_message:
          'あなたのパスワードは更新されたセキュリティ要件を満たしていません。自動的にログアウトされました。システムへのアクセスを回復するために、新しいパスワードを作成してください。',
        alert_change_password_button: 'パスワードを更新する',
      },
    },
  },
};
</script>

<style lang="scss">
.change-password-form {
  &__container {
    margin: 0 auto;
    width: 273px;
    background: white;
    padding: 30px 60px 15px 60px;
    color: #424c61;
    box-shadow: 0 0 8px 0 rgba(232, 237, 250, 0.6), 0 2px 4px 0 rgba(232, 237, 250, 0.5);
    text-align: center;

    @media screen and (max-width: 500px) {
      padding: 15px 30px 15px 30px;
    }
  }

  &__header {
    margin-bottom: 20px;
    font-size: 14px;
  }

  &__title {
    font-size: 22px;
    font-weight: 400;
    margin: 0;
  }

  &__btn {
    &--fluid {
      width: 100%;
    }
  }

  &__message {
    margin: 30px 0;
  }

  &__footer {
    font-size: 14px;
    padding: 30px 0 15px 0;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 4px;

    &--ja {
      flex-direction: row-reverse;
    }
  }

  .el-form-item__error {
    position: relative;
  }
}
</style>
